import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, RefreshCw, X } from 'lucide-react';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DEFAULT_CENTER = [25.2048493, 55.2707828];

const ClickHandler = ({ onPick }) => {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const InvalidateOnReady = () => {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        map.invalidateSize();
      } catch {
        /* ignore */
      }
    }, 200);
    return () => clearTimeout(t);
  }, [map]);
  return null;
};

const FlyTo = ({ position, zoom = 15 }) => {
  const map = useMap();
  useEffect(() => {
    if (!position || position.length !== 2) return;
    const [la, ln] = position;
    if (!Number.isFinite(la) || !Number.isFinite(ln)) return;
    map.flyTo([la, ln], zoom, { duration: 0.8 });
  }, [map, position, zoom]);
  return null;
};

const searchNominatim = async (query, signal) => {
  const url =
    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}` +
    '&format=json&limit=6&addressdetails=0';
  const resp = await fetch(url, {
    headers: {
      Accept: 'application/json',
      'Accept-Language': 'en,ar',
    },
    signal,
  });
  if (!resp.ok) throw new Error('Search failed');
  const data = await resp.json();
  if (!Array.isArray(data)) return [];
  return data
    .map((item) => {
      const lat = Number(item.lat);
      const lng = Number(item.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return {
        lat,
        lng,
        label: item.display_name || query,
      };
    })
    .filter(Boolean);
};

/**
 * Pick office LAT/LNG on a map for Company Profile → Coordinates.
 */
const OfficeLocationMap = ({ lat, lng, onChange }) => {
  const parsedLat = Number(lat);
  const parsedLng = Number(lng);
  const hasPoint =
    Number.isFinite(parsedLat) && Number.isFinite(parsedLng) && !(parsedLat === 0 && parsedLng === 0);
  const position = hasPoint ? [parsedLat, parsedLng] : DEFAULT_CENTER;
  const [markerPos, setMarkerPos] = useState(position);
  const [flyTarget, setFlyTarget] = useState(null);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (hasPoint) setMarkerPos([parsedLat, parsedLng]);
  }, [hasPoint, parsedLat, parsedLng]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handlePick = (la, ln, label) => {
    const nextLat = Number(Number(la).toFixed(6));
    const nextLng = Number(Number(ln).toFixed(6));
    setMarkerPos([nextLat, nextLng]);
    setFlyTarget([nextLat, nextLng]);
    onChange({ LAT: String(nextLat), LNG: String(nextLng) });
    if (label) setQuery(label);
  };

  const runSearch = async (q, autoSelectFirst = false) => {
    const term = String(q || '').trim();
    if (term.length < 3) {
      setSuggestions([]);
      setSearchError(term ? 'Type at least 3 characters' : '');
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSearching(true);
    setSearchError('');
    try {
      const results = await searchNominatim(term, controller.signal);
      if (controller.signal.aborted) return;
      setSuggestions(results);
      setShowSuggestions(true);
      if (!results.length) {
        setSearchError('No locations found');
        return;
      }
      if (autoSelectFirst) {
        const first = results[0];
        handlePick(first.lat, first.lng, first.label);
        setShowSuggestions(false);
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error(err);
      setSearchError('Search failed. Try again.');
      setSuggestions([]);
    } finally {
      if (!controller.signal.aborted) setSearching(false);
    }
  };

  const onQueryChange = (value) => {
    setQuery(value);
    setSearchError('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(value, false);
    }, 450);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    runSearch(query, true);
  };

  return (
    <div className="space-y-3">
      <div className="relative" ref={wrapRef}>
        <form onSubmit={onSearchSubmit} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              placeholder="Search office location (e.g. Business Bay, Dubai)"
              className="w-full pl-10 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            {query ? (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                onClick={() => {
                  setQuery('');
                  setSuggestions([]);
                  setSearchError('');
                  setShowSuggestions(false);
                }}
                title="Clear"
              >
                <X className="w-4 h-4" />
              </button>
            ) : null}
          </div>
          <button
            type="submit"
            disabled={searching || String(query).trim().length < 3}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Search
          </button>
        </form>

        {showSuggestions && suggestions.length > 0 ? (
          <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
            {suggestions.map((item, idx) => (
              <li key={`${item.lat}-${item.lng}-${idx}`}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2.5 text-sm text-gray-800 hover:bg-[#f0f9f4] border-b border-gray-50 last:border-0"
                  onClick={() => {
                    handlePick(item.lat, item.lng, item.label);
                    setShowSuggestions(false);
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {searchError ? <p className="mt-1 text-xs text-red-600">{searchError}</p> : null}
      </div>

      <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: 360 }}>
        <MapContainer
          center={markerPos}
          zoom={hasPoint ? 14 : 11}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <InvalidateOnReady />
          <FlyTo position={flyTarget} />
          <ClickHandler onPick={handlePick} />
          <Marker
            position={markerPos}
            draggable
            eventHandlers={{
              dragend: (e) => {
                const p = e.target.getLatLng();
                handlePick(p.lat, p.lng);
              },
            }}
          />
        </MapContainer>
      </div>
      <p className="text-xs text-gray-500">
        Search for an address, or click the map / drag the marker to set the office location.
      </p>
    </div>
  );
};

export default OfficeLocationMap;
